import i18n from '@/utils/i18n';
import React from 'react';
import { AnimatedSkeletonLoader } from '@usepepper/ui/atoms/animated-skeleton-loader';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

interface AccountDetailARCardProps {
  loading?: boolean;
  overdueBalanceInMicros: number;
  totalBalanceInMicros: number;
}

// Heights mirror the rendered typography font sizes below: paragraph-sm (12)
// for labels, heading-xs (14) for values. Bump these if the variants change.
const SKELETON_LABEL_WIDTH = 64;
const SKELETON_LABEL_HEIGHT = 12;
const SKELETON_VALUE_WIDTH = 110;
const SKELETON_VALUE_HEIGHT = 14;

export const AccountDetailARCard: React.FC<AccountDetailARCardProps> = ({
  loading,
  overdueBalanceInMicros,
  totalBalanceInMicros,
}) => {
  const [styles, theme] = useStyles();

  if (loading) {
    return (
      <View style={styles.card}>
        <View style={styles.row}>
          <AnimatedSkeletonLoader
            borderRadius={theme.sk.radii.radiusRound}
            height={SKELETON_LABEL_HEIGHT}
            width={SKELETON_LABEL_WIDTH}
          />
          <AnimatedSkeletonLoader
            borderRadius={theme.sk.radii.radiusRound}
            height={SKELETON_VALUE_HEIGHT}
            width={SKELETON_VALUE_WIDTH}
          />
        </View>
        <View style={[styles.row, styles.rowLast]}>
          <AnimatedSkeletonLoader
            borderRadius={theme.sk.radii.radiusRound}
            height={SKELETON_LABEL_HEIGHT}
            width={SKELETON_LABEL_WIDTH}
          />
          <AnimatedSkeletonLoader
            borderRadius={theme.sk.radii.radiusRound}
            height={SKELETON_VALUE_HEIGHT}
            width={SKELETON_VALUE_WIDTH}
          />
        </View>
      </View>
    );
  }

  const formattedTotal = formatCurrency(totalBalanceInMicros, null, {
    currencyDisplay: 'narrowSymbol',
  });

  const formattedOverdue = formatCurrency(overdueBalanceInMicros, null, {
    currencyDisplay: 'narrowSymbol',
  });

  return (
    <View
      accessibilityLabel={i18n.t(
        'sales_portal.account_detail.ar.summary_label',
      )}
      style={styles.card}
    >
      <View style={styles.row}>
        <Typography
          color={theme.sk.color.text.secondary}
          variant="paragraph-sm"
        >
          {i18n.t('sales_portal.account_detail.ar.total_due')}
        </Typography>
        <Typography variant="heading-xs">{formattedTotal}</Typography>
      </View>
      <View style={[styles.row, styles.rowLast]}>
        <Typography
          color={theme.sk.color.text.secondary}
          variant="paragraph-sm"
        >
          {i18n.t('sales_portal.account_detail.ar.past_due')}
        </Typography>
        <Typography
          color={
            overdueBalanceInMicros > 0
              ? theme.sk.color.text.error
              : theme.sk.color.text.primary
          }
          variant="heading-xs"
        >
          {formattedOverdue}
        </Typography>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  card: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  row: {
    borderBottomColor: theme.sk.color.divider.default,
    borderBottomWidth: 1,
    gap: theme.sk.spacing.spaceXsmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  rowLast: {
    borderBottomWidth: 0,
  },
}));
